/**
 * ShareLayout Component - Phase 5a
 *
 * Layout for public share routes (/s/:shareToken/*)
 * Similar to DashboardLayout but for unauthenticated access
 *
 * Key Differences from DashboardLayout:
 * - No PropertyProvider (uses ShareContext instead)
 * - No datasource selector (share links show specific property)
 * - Includes ShareBanner at top
 * - Fetches data using share token
 */

import { Outlet } from "react-router-dom";
import Header from "@/components/layout/Header";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useShare } from "@/lib/contexts/ShareContext";
import { useURLState } from "@/hooks/useURLState";
import { useQuery } from "@tanstack/react-query";
import { getTrafficMetrics, getEventMetrics } from "@/lib/api/bridge";
import FilterBar from "@/components/dashboard/FilterBar";
import { LoadingState, InlineNotice } from "@/components/shared/layout";
import { Card, CardContent } from "@/components/ui/card";
import { ShareBanner } from "./ShareBanner";
import { HugeiconsIcon } from "@hugeicons/react"
import { AlertCircleIcon } from "@hugeicons/core-free-icons"

/**
 * ShareLayout
 *
 * Provides dashboard layout for shared views
 * Uses ShareContext for property data
 */
export default function ShareLayout() {
  const { propertyId, token, magicId, password, property } = useShare();
  const { state: urlState } = useURLState();

  // Show filter bar only on dashboards, experiments, and heatmaps
  const shouldShowFilterBar = true; // Share views always show filter bar

  // Fetch traffic metrics using JWT token from magic link validation
  const {
    data: trafficMetrics,
    isLoading: isTrafficLoading,
    error: trafficError,
  } = useQuery({
    queryKey: [
      "shareTrafficMetrics",
      propertyId,
      token,
      urlState.period,
      urlState.filters,
    ],
    queryFn: () =>
      getTrafficMetrics(
        propertyId || '',
        urlState.period,
        urlState.filters,
        [], // No datasource filtering for share links
        token || '' // Pass JWT token for authentication
      ),
    enabled: !!propertyId && !!token,
    placeholderData: (previousData) => previousData,
  });

  // Fetch event metrics using JWT token
  const {
    data: eventMetrics,
    isLoading: isEventLoading,
    error: eventError,
  } = useQuery({
    queryKey: [
      "shareEventMetrics",
      propertyId,
      token,
      urlState.period,
      urlState.filters,
    ],
    queryFn: () =>
      getEventMetrics(
        propertyId || '',
        urlState.period,
        urlState.filters,
        [], // No datasource filtering for share links
        'pages', // view type
        token || '' // Pass JWT token for authentication
      ),
    enabled: !!propertyId && !!token,
    placeholderData: (previousData) => previousData,
  });

  const isLoading = isTrafficLoading || isEventLoading;
  const error = trafficError || eventError;

  // Transform the fetched metrics into the structure that Traffic component expects
  // Traffic component expects: dashboardData.metrics with aggregate, trends, etc.
  const currentDomain = property?.domain || '';

  const dashboardData = trafficMetrics ? {
    stats: trafficMetrics,
    metrics: trafficMetrics.metrics || {},
    formStats: {},
    clickStats: {},
    propertyId: propertyId || '',
    currentDomain,
  } : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <LoadingState message="Loading dashboard..." size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <InlineNotice
              variant="error"
              message="Error Loading Dashboard"
              description={(error as Error).message || "Please try again or contact the dashboard owner."}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <SidebarProvider>
    <div className="flex flex-col min-h-screen w-full bg-background overflow-hidden">
      {/* Share Banner - indicates this is a shared view */}
      <ShareBanner />

      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Filter Bar - only period and basic filters for share views */}
        {shouldShowFilterBar && (
          <div className="border-b border-border/20 bg-background shrink-0">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3">
              <FilterBar />
            </div>
          </div>
        )}

        {/* Scrollable content area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 bg-surface">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <Outlet
          context={{
            dashboardData,
            trafficMetrics,
            eventMetrics,
            isLoading,
            isShareMode: true,
            magicId,
            password,
            token,
            propertyId,
            property,
            currentDomain,
            period: urlState.period,
            filters: urlState.filters,
            sources: [],
            activeDataSources: [],
            refetchData: () => {},
            showRevenue: false,
            isAggregateMode: false,
            enabledProperties: [],
            workspaceId: undefined,
          }}
        />
          </div>
      </main>
      </div>
    </div>
    </SidebarProvider>
  );
}
